import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
	signal,
	viewChild,
} from '@angular/core';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { filter, map, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { Transport, TransportKind, TransportSide, TransportSideType } from '@/new-transport/_models/transport.model';
import { TransportTemplateApiService } from '@/new-transport/transport-template/_api/transport-template.api.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatSortModule } from '@angular/material/sort';
import { TemplateDuplicateComponent } from '@/new-transport/transport-template/template-duplicate/template-duplicate.component';

@Component({
	selector: 'app-transport-template',
	templateUrl: './transport-template.component.html',
	styleUrl: './transport-template.component.scss',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NavbarModule,
		MatButton,
		FormsModule,
		MatTable,
		MatHeaderCell,
		MatCell,
		MatColumnDef,
		MatHeaderCellDef,
		MatCellDef,
		MatHeaderRow,
		MatRow,
		MatHeaderRowDef,
		MatRowDef,
		MatPaginator,
		MatSortModule,
	],
})
export class TransportTemplateComponent implements OnInit, AfterViewInit {
	filterValue = signal('');

	readonly displayedColumns = ['id', 'name', 'side', 'show', 'duplicate', 'update-name'];
	readonly matData = new MatTableDataSource<Transport>();
	readonly TransportSide = TransportSide;

	private readonly sort = viewChild<MatSort>(MatSort);
	private readonly paginator = viewChild<MatPaginator>(MatPaginator);

	private readonly templateApiService = inject(TransportTemplateApiService);
	private readonly infobox = inject(InfoboxService);
	private readonly dialog = inject(MatDialog);
	private readonly cdr = inject(ChangeDetectorRef);

	ngOnInit(): void {
		this.templateApiService.getTemplates().subscribe((templates) => (this.matData.data = templates));
	}

	ngAfterViewInit(): void {
		this.matData.sort = this.sort();
		this.matData.sortingDataAccessor = (item: Transport, property: string) => {
			return item[property].toString().toLowerCase();
		};
		this.matData.paginator = this.paginator();
	}

	refreshFilter(): void {
		if (this.filterValue().length < 3 && this.filterValue().length > 0) {
			return;
		}
		this.matData.filter = this.filterValue().trim().toLowerCase();
	}

	createTemplate(side: TransportSideType): void {
		window.open(`transport/map/new?side=${side}&kind=${TransportKind.TEMPLATE}`);
	}

	show(id: number | string): void {
		window.open(`transport/${id}/map/new`);
	}

	async updateName(id: number, name: string): Promise<void> {
		const component = await import('../update-transport-name/update-transport-name.component').then(
			(c) => c.UpdateTransportNameComponent,
		);

		this.dialog
			.open(component, {
				data: { transportId: id, transportName: name },
				width: '50vw',
				maxWidth: '700px',
			})
			.afterClosed()
			.pipe(filter((value) => typeof value === 'string'))
			.subscribe((value) => {
				const row = this.matData.data.find((d) => d.id === id);
				row.name = value;
				this.cdr.detectChanges();
			});
	}

	duplicate(transportTemplateId: number): void {
		const transportTemplate = this.matData.data.find((data) => data.id === transportTemplateId);

		if (!transportTemplate) {
			console.error(`Transport template with ID ${transportTemplateId} not found.`);
			return;
		}

		const { name } = transportTemplate;

		this.dialog
			.open(TemplateDuplicateComponent, {
				data: { name, transport: transportTemplate },
				minWidth: '380px',
			})
			.afterClosed()
			.pipe(
				filter(Boolean),
				switchMap((transportId: number) =>
					this.templateApiService.getTemplates().pipe(map((templates) => ({ templates, transportId }))),
				),
			)
			.subscribe(({ templates, transportId }) => {
				this.matData.data = templates;
				window.open(`/transport/${transportId}/table/new`);
			});
	}
}
